import { gql } from 'graphql-tag';
import { pick, set } from 'lodash';
import { ActionTree, GetterTree, MutationTree } from 'vuex';
import { RootState } from '~/store';

const initialState = () => ({
  id: null,
  controller: null,
  data: {
    selectedGroup: null
  },
  table: {
    data: {
      count: 0,
      results: []
    },
    headers: [
      {
        text: 'Category',
        align: 'center',
        sortable: true,
        value: 'name'
      },
      {
        text: '',
        align: 'center',
        sortable: false,
        value: 'actions'
      }
    ],
    footer: {
      'items-per-page-options': [10, 25, 50, 100],
      'show-current-page': true,
      'show-first-last-page': true
    },
    filter: {
      keyword: ''
    },
    options: {
      page: 1,
      itemsPerPage: 10,
      sortBy: ['name'],
      sortDesc: [false],
      groupBy: [],
      groupDesc: [],
      multiSort: false,
      mustSort: true
    }
  },
  query: {
    where: {},
    sort: {
      name: 'ASC'
    },
    offset: 0,
    limit: 10
  },
  loading: {
    findAll: false,
    findOne: false,
    add: false,
    edit: false,
    delete: false
  }
});

export const state = () => initialState();

export type ProductGroupState = ReturnType<typeof state>;

export const getters: GetterTree<ProductGroupState, RootState> = {
  getId: ({ id }) => id,
  getQuery: ({ query }) => query,
  getTable: ({ table }) => table,
  getSelectedGroup: ({ data }) => data.selectedGroup,
  getLoading: ({ loading }) => loading
};

export const mutations: MutationTree<ProductGroupState> = {
  changeId: (state, id) => (state.id = id),
  changeData: (state, data) => (state.table.data = data),
  changeSelectedGroup: (state, data) => (state.data.selectedGroup = data),
  changeQuery: (state, query) => Object.assign(state.query, query),
  changeTableFilter: (state, filter) => {
    Object.assign(state.table, { filter });
    Object.assign(state.query, {
      where: {
        name: filter.keyword
      }
    });
  },
  changeTableOptions: (state, options) => {
    Object.assign(state.table, { options });
    Object.assign(state.query, {
      sort: set({}, options.sortBy[0], options.sortDesc[0] ? 'DESC' : 'ASC'),
      limit: options.itemsPerPage,
      offset: (options.page - 1) * options.itemsPerPage
    });
  },
  setLoading: (state, loading: ProductGroupState['loading']) =>
    (state.loading = Object.assign({}, state.loading, loading)),
  setController: (state, controller) => (state.controller = controller),
  resetState: (state) => Object.assign(state, initialState())
};

export const actions: ActionTree<ProductGroupState, RootState> = {
  /**
   * Fetch merchants.
   *
   * @param {*} { commit, state, dispatch }
   * @param {{ standalone: boolean; query: ProductGroupState['query'] }} [props] Prop options;
   *
   * @description `
   *  props to be used on other components without disturbing the state
   * `
   * @return {*}
   */
  async findAll(
    { commit, state, dispatch },
    props?: { standalone: boolean; query: ProductGroupState['query'] }
  ) {
    if (process.server) return;

    dispatch('controllerInit');

    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { findAll: true });

      let variables = state.query;
      if (props?.standalone) variables = props.query;
      const { data } = await api.query({
        query: gql`
          query GroupFindAll(
            $where: PurchaseProductGroupQueryFilter
            $sort: PurchaseProductGroupQuerySortBy
            $limit: Int
            $offset: Int
          ) {
            Purchase {
              Product {
                Group {
                  findAll(
                    where: $where
                    sort: $sort
                    limit: $limit
                    offset: $offset
                  ) {
                    count
                    results {
                      id
                      name
                    }
                  }
                }
              }
            }
          }
        `,
        variables,
        context: {
          fetchOptions: {
            signal: state.controller.signal
          }
        }
      });
      const groups = data?.Purchase?.Product?.Group?.findAll;
      commit('setLoading', { findAll: false });
      if (props?.standalone) {
        return { status: true, message: null, data: groups };
      } else {
        commit('changeData', pick(groups, ['count', 'results']));
      }
    } catch (error) {
      commit('setLoading', { findAll: false });
      if (props?.standalone) {
        return { status: false, message: error, data: null };
      } else {
        commit('changeData', { count: 0, results: [] });
      }
      if (!/signal is aborted without reason/i.test(error.toString())) {
        emitter.emit('TOAST', error);
      }
    }
  },

  /**
   * Find one merchant.
   */
  async findOne(
    { commit, state, dispatch },
    props?: { id: string; standalone?: boolean }
  ) {
    if (process.server) return;

    dispatch('controllerInit');

    const api = window.$nuxt.$options.$api;
    const emitter = window.$nuxt.$options.$emitter;

    try {
      commit('setLoading', { findOne: true });
      commit('changeSelectedGroup', null);

      const variables = { id: props.id };
      const { data } = await api.query({
        query: gql`
          query GroupFindOne($id: String!) {
            Purchase {
              Product {
                Group {
                  findOne(id: $id) {
                    id
                    name
                  }
                }
              }
            }
          }
        `,
        variables,
        context: {
          fetchOptions: {
            signal: state.controller.signal
          }
        }
      });
      const group = data?.Purchase?.Product?.Group?.findOne;
      commit('setLoading', { findOne: false });
      if (props?.standalone) {
        return { status: true, message: null, data: group };
      } else {
        commit('changeSelectedGroup', group);
      }
    } catch (error) {
      commit('setLoading', { findOne: false });
      if (props?.standalone) {
        return { status: false, message: error, data: null };
      } else {
        commit('changeSelectedGroup', null);
      }
      if (!/signal is aborted without reason/i.test(error.toString())) {
        emitter.emit('TOAST', error);
      }
    }
  },

  /**
   * Add group mutation.
   */
  async addGroup({ commit }, { form }: { form: Record<string, any> }) {
    if (process.server) return;

    const api = window.$nuxt.$options.$api;

    try {
      commit('setLoading', { add: true });

      await api.mutate({
        mutation: gql`
          mutation GroupAdd($form: AddPurchaseProductGroupForm) {
            Purchase {
              Product {
                Group {
                  add(form: $form) {
                    id
                  }
                }
              }
            }
          }
        `,
        variables: { form }
      });

      commit('setLoading', { add: false });
      return { status: true, message: '' };
    } catch (error) {
      commit('setLoading', { add: false });
      return { status: false, message: error };
    }
  },

  /**
   * Edit group mutation.
   */
  async editGroup(
    { commit },
    { id, form }: { id: string; form: Record<string, any> }
  ) {
    if (process.server) return;

    const api = window.$nuxt.$options.$api;

    try {
      commit('setLoading', { edit: true });

      await api.mutate({
        mutation: gql`
          mutation GroupEdit(
            $id: String!
            $form: EditPurchaseProductGroupForm
          ) {
            Purchase {
              Product {
                Group {
                  edit(id: $id, form: $form) {
                    id
                  }
                }
              }
            }
          }
        `,
        variables: { id, form }
      });

      commit('setLoading', { edit: false });
      return { status: true, message: '' };
    } catch (error) {
      commit('setLoading', { edit: false });
      return { status: false, message: error };
    }
  },

  /**
   * Delete group mutation.
   */
  async deleteGroup({ commit }, { id }: { id: string }) {
    if (process.server) return;

    const api = window.$nuxt.$options.$api;

    try {
      commit('setLoading', { delete: true });

      await api.mutate({
        mutation: gql`
          mutation GroupDelete($id: String!) {
            Purchase {
              Product {
                Group {
                  delete(id: $id)
                }
              }
            }
          }
        `,
        variables: { id }
      });

      commit('setLoading', { delete: false });
      return { status: true, message: '' };
    } catch (error) {
      commit('setLoading', { delete: false });
      return { status: false, message: error };
    }
  },

  /**
   * Creates a new AbortController object instance.
   */
  controllerInit({ commit, state }) {
    if (!state.controller) commit('setController', new AbortController());
  },

  /**
   * Controller abort for canceled request.
   */
  controllerAbort({ commit, state }) {
    if (state.controller) {
      state.controller.abort();
      commit('setController', null);
    }
  }
};
